import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs, H4 } from '../../../../AbstractElements';
import dynamicFormFieldApi from '../../../../api/dynamicFormField';
import FieldCard from './FieldCard';
import FieldModal from './FieldModal';
import FieldFilters from './FieldFilters';

const DynamicFormFieldContain = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal
  const [modal, setModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    fieldOrder: 1,
    isRequired: false,
    isActive: true,
    fieldConfig: {},
    validationRules: {}
  });

  // Load fields
  useEffect(() => {
    loadFields();
  }, [typeFilter, statusFilter]);

  const loadFields = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (typeFilter !== 'all') params.fieldType = typeFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await dynamicFormFieldApi.getAllFields(params);
      setFields(response.data?.fields || response.fields || response.data || response);
    } catch (err) {
      console.error('❌ Error loading fields:', err);
      setError('Không thể tải danh sách fields');
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingField(null);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      fieldOrder: 1,
      isRequired: false,
      isActive: true,
      fieldConfig: {},
      validationRules: {}
    });
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      fieldName: field.fieldName,
      fieldLabel: field.fieldLabel,
      fieldType: field.fieldType,
      fieldOrder: field.fieldOrder || 1,
      isRequired: field.isRequired || false,
      isActive: field.isActive,
      fieldConfig: field.fieldConfig || {},
      validationRules: field.validationRules || {}
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      // Prepare validation rules based on field config
      const validationRules = {
        ...formData.validationRules,
        required: formData.isRequired
      };
      
      if (formData.fieldConfig.minLength) {
        validationRules.minLength = formData.fieldConfig.minLength;
      }
      if (formData.fieldConfig.maxLength) {
        validationRules.maxLength = formData.fieldConfig.maxLength;
      }
      if (formData.fieldType === 'email') {
        validationRules.email = true;
      }
      
      const dataToSend = {
        ...formData,
        validationRules
      };
      
      if (editingField) {
        await dynamicFormFieldApi.updateField(editingField.id, dataToSend);
        setSuccess('Cập nhật field thành công!');
      } else {
        await dynamicFormFieldApi.createField(dataToSend);
        setSuccess('Tạo field thành công!');
      }
      
      toggleModal();
      loadFields();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error saving field:', err);
      setError('Không thể lưu field');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa field này?')) return;
    
    try {
      await dynamicFormFieldApi.deleteField(id);
      setSuccess('Xóa field thành công!');
      loadFields();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error deleting field:', err);
      setError('Không thể xóa field');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const field = fields.find(f => f.id === id);
      await dynamicFormFieldApi.updateField(id, { isActive: !field.isActive });
      setSuccess('Cập nhật trạng thái thành công!');
      loadFields();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      setError('Không thể cập nhật trạng thái');
    }
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Quiz' title='Form Fields' mainTitle='Form Fields' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải fields...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Quiz' title='Form Fields' mainTitle='Quản lý Dynamic Form Fields' />
      <Container fluid={true}>
        {/* Success/Error Messages */}
        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md="6">
                <H4 className="text-dark mb-0">
                  <i className="fa fa-wpforms me-2 text-primary"></i>
                  Quản lý Dynamic Form Fields
                </H4>
              </Col>
              <Col md="6" className="text-end">
                <Button color="primary" onClick={toggleModal}>
                  <i className="fa fa-plus me-2"></i>
                  Tạo Field Mới
                </Button>
              </Col>
            </Row>

            {/* Filters */}
            <FieldFilters 
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </CardBody>
        </Card>

        {/* Fields List */}
        <Row>
          {fields.length === 0 ? (
            <Col>
              <Card className="text-center py-5">
                <CardBody>
                  <i className="fa fa-wpforms text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="text-muted mt-3">Chưa có field nào</p>
                </CardBody>
              </Card>
            </Col>
          ) : (
            fields.map((field) => (
              <Col lg="6" key={field.id}>
                <FieldCard 
                  field={field}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              </Col>
            ))
          )}
        </Row>

        {/* Create/Edit Modal */}
        <FieldModal 
          isOpen={modal}
          toggle={toggleModal}
          editingField={editingField}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
        />
      </Container>
    </Fragment>
  );
};

export default DynamicFormFieldContain;

